import type { AuthState, User } from '../types';
import { dbgV, dbgI, dbgW, dbgE } from './debug';

const COMP = 'auth';

const TOKEN_KEY = 'responsekit_token';
const USER_KEY = 'responsekit_user';
const REFRESH_KEY = 'responsekit_refresh_token';

// Cached Sentinel logout URL — set by Login.tsx after /api/auth/login call (derived from authorize_url).
// Falls back to a derived value if not yet populated.
let _sentinelLogoutUrl: string | null = null;

export function setSentinelLogoutUrl(url: string): void {
  dbgV(COMP, 'setSentinelLogoutUrl', { url });
  _sentinelLogoutUrl = url;
}

function getSentinelLogoutUrl(): string {
  if (_sentinelLogoutUrl) return _sentinelLogoutUrl;
  // Fallback: derive from the stored OIDC issuer saved during login PKCE flow.
  // sessionStorage clears on tab close / page refresh; localStorage persists.
  const issuer = sessionStorage.getItem('oidc_issuer') ?? localStorage.getItem('oidc_issuer');
  if (issuer) return `${issuer.replace(/\/$/, '')}/oidc/end_session`;
  // Last resort: same-origin fallback (only works when IdP is co-hosted).
  const origin = window.location.origin;
  dbgW(COMP, 'getSentinelLogoutUrl: no cached URL, falling back to same-origin /oidc/end_session', { origin });
  return `${origin}/oidc/end_session`;
}

/**
 * The IdP-hosted passkey enrollment page. Passkey registration MUST run on the
 * IdP origin (the WebAuthn RP-ID is the IdP host, e.g. id-dev.responsekit.net),
 * not on the app host — a credential enrolled from the app origin would be bound
 * to the wrong RP and rejected at login. So we send the user to the IdP's own
 * /auth/passkey-setup page, derived from the OIDC issuer (same source as the
 * logout URL). Returns null if the issuer isn't known yet (no cached login).
 */
export function getSentinelPasskeySetupUrl(): string | null {
  const issuer = sessionStorage.getItem('oidc_issuer') ?? localStorage.getItem('oidc_issuer');
  if (!issuer) return null;
  return `${issuer.replace(/\/$/, '')}/auth/passkey-setup`;
}

// Refresh when less than 60 seconds remain on the 5-minute access token.
// Must be well below jwt_expire_minutes (5) or we thrash; must be > typical
// request latency so proactive refresh actually fires before the token dies.
const REFRESH_THRESHOLD_SEC = 60;

export function saveAuth(token: string, user: User, refreshToken?: string) {
  dbgI(COMP, 'saveAuth: writing tokens to localStorage', {
    email: user.email,
    hasRefresh: !!refreshToken,
    tokenLength: token.length,
  });
  const prevToken = localStorage.getItem(TOKEN_KEY);
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (refreshToken) {
    localStorage.setItem(REFRESH_KEY, refreshToken);
  }
  // Verify write succeeded (guards against storage quota exceeded)
  const verified = !!localStorage.getItem(TOKEN_KEY) && !!localStorage.getItem(USER_KEY);
  if (!verified) {
    dbgE(COMP, 'saveAuth: localStorage write verification FAILED — storage may be full or blocked');
  } else {
    dbgV(COMP, 'saveAuth: localStorage write verified OK');
  }
  // Notify subscribers (ModuleProvider, WebSocket hooks) when the access token
  // actually changes. Covers initial login (AuthCallback), demo session swap,
  // and profile updates that re-issue a token. refreshSession() also dispatches
  // this event for the silent-refresh path.
  if (prevToken !== token) {
    try {
      window.dispatchEvent(new Event('rk:token-changed'));
    } catch {
      // ignore — environments without window don't need the event
    }
  }
}

export function clearAuth() {
  dbgI(COMP, 'clearAuth: removing all auth keys from localStorage');
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem('oidc_issuer');
  dbgV(COMP, 'clearAuth: done', {
    tokenGone: !localStorage.getItem(TOKEN_KEY),
    userGone: !localStorage.getItem(USER_KEY),
    refreshGone: !localStorage.getItem(REFRESH_KEY),
  });
}

// How long fullLogout() waits for POST /api/auth/logout before giving up and
// navigating anyway (#1393 PR 2). Short enough that a hung/unreachable
// backend can't stall the user on the logout screen, long enough to survive
// ordinary latency for a same-origin POST.
const LOGOUT_REQUEST_TIMEOUT_MS = 3000;

// Overall deadline for the WHOLE server-side logout attempt (refresh +
// retry + both POSTs), not just one fetch. LOGOUT_REQUEST_TIMEOUT_MS above
// only bounds a single POST /api/auth/logout call; refreshSession() ->
// doRefresh() calls fetch('/api/auth/refresh') with NO timeout of its own
// (intentionally — other callers of refreshSession(), like the 401-retry
// path in client.ts, should keep waiting on a slow-but-alive backend). If
// that refresh call hangs, attemptServerLogout() could otherwise stall
// fullLogout() forever. This deadline wraps the attempt from the outside
// instead, so clearAuth() and the navigation always happen within it
// regardless of what refresh or logout are doing.
const SERVER_LOGOUT_DEADLINE_MS = 5000;

/**
 * Race `promise` against a `ms`-long timer. Resolves with the promise's
 * value if it wins, or with the sentinel 'timeout' if the deadline elapses
 * first — the loser keeps running in the background (nothing here can
 * cancel an in-flight fetch with no AbortController of its own), it's just
 * no longer awaited.
 */
function withDeadline<T>(promise: Promise<T>, ms: number): Promise<T | 'timeout'> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<'timeout'>(resolve => {
    timer = setTimeout(() => resolve('timeout'), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * POST /api/auth/logout with the given bearer token.
 *
 * `keepalive: true` lets the request survive the page navigation that
 * follows it — without it, the browser can cancel an in-flight fetch the
 * instant `window.location.href` is set, so the revoke never reaches the
 * server (#1393 defect 8). A short AbortController timeout bounds how long
 * we'll wait: logout must never get stuck on a hung/unreachable backend.
 *
 * Returns the Response, or null on a network error/timeout — callers must
 * treat null as "couldn't confirm, move on" and still clear local state.
 */
async function postLogoutRequest(token: string): Promise<Response | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOGOUT_REQUEST_TIMEOUT_MS);
  try {
    const resp = await fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      keepalive: true,
      signal: controller.signal,
    });
    dbgI(COMP, 'fullLogout: /api/auth/logout responded', { status: resp.status });
    return resp;
  } catch (err) {
    dbgW(COMP, 'fullLogout: /api/auth/logout request failed (non-fatal)', { error: String(err) });
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Refresh via the app's existing refreshSession() helper, then report the
 * NEW access token if it actually changed. Returns null if refreshSession()
 * failed, or (defensively) if it "succeeded" without producing a different
 * token — either way the caller falls back to its already-known token.
 */
async function refreshForLogout(originalToken: string): Promise<string | null> {
  const ok = await refreshSession();
  if (!ok) return null;
  const newToken = localStorage.getItem(TOKEN_KEY);
  if (!newToken || newToken === originalToken) return null;
  return newToken;
}

/**
 * Attempt server-side revocation for one logout. Handles both known failure
 * modes from #1393 defect 8:
 *   - the stored access token is already expired (60-min TTL, easy to hit
 *     from an idle tab) — refresh BEFORE sending, so the request isn't
 *     doomed to a 401 that revokes nothing;
 *   - the token looked valid locally but the server answers 401 anyway
 *     (clock skew, revoked out from under us, etc.) — refresh and retry
 *     exactly once.
 * refreshSession() is called at most once per logout, whichever path fires.
 * Never throws: a network error, a timeout, or a refresh failure just means
 * the caller proceeds to clear local state and navigate regardless.
 */
async function attemptServerLogout(token: string): Promise<void> {
  let activeToken = token;
  let refreshAttempted = false;

  const exp = getTokenExp(token);
  const tokenExpired = exp !== null && Date.now() / 1000 >= exp;
  if (tokenExpired) {
    dbgI(COMP, 'fullLogout: access token expired — refreshing before logout');
    refreshAttempted = true;
    const refreshed = await refreshForLogout(token);
    if (refreshed) activeToken = refreshed;
  }

  const resp = await postLogoutRequest(activeToken);
  if (!resp) return; // network error/timeout — nothing more to do here

  if (resp.status === 401 && !refreshAttempted) {
    dbgW(COMP, 'fullLogout: /api/auth/logout returned 401 — refreshing and retrying once');
    const refreshed = await refreshForLogout(token);
    if (refreshed) {
      await postLogoutRequest(refreshed);
    } else {
      dbgW(COMP, 'fullLogout: refresh failed or produced no new token — giving up on server logout');
    }
  }
}

// Module-level singleton, mirroring _refreshPromise below: concurrent
// fullLogout() callers (e.g. a double-click, or LockScreen and Navbar both
// firing in the same tick) share one in-flight logout instead of each
// issuing their own POST /api/auth/logout and racing to navigate.
let _fullLogoutPromise: Promise<void> | null = null;

// Tripped when doFullLogout's overall deadline (SERVER_LOGOUT_DEADLINE_MS)
// fires while attemptServerLogout() -- specifically the refresh it may have
// kicked off -- is still pending (review follow-up on #1473, round 2).
//
// withDeadline() only stops AWAITING attemptServerLogout; it cannot cancel
// the underlying fetch('/api/auth/refresh'), since doRefresh() has no
// AbortController/timeout of its own (deliberately -- other refreshSession()
// callers, e.g. client.ts's 401 retry, must keep waiting on a slow-but-alive
// backend). So once the deadline wins the race, attemptServerLogout's
// refresh call keeps running as an abandoned promise chain: if it later
// resolves successfully, doRefresh()'s normal success path would persist a
// fresh token pair via saveAuth() -- AFTER doFullLogout has already run
// clearAuth() and started navigating away. Tokens would reappear after
// sign-out.
//
// This flag is checked inside doRefresh() (see below) and skips exactly that
// persist step -- for THIS abandoned call and for any other caller (e.g. a
// concurrent client.ts reactive refresh) that happens to resolve after the
// same point. It is only ever set here, in the deadline-exceeded branch --
// never unconditionally at the top of doFullLogout -- because the
// EXPECTED, within-deadline refresh-then-retry path (attemptServerLogout's
// own refreshForLogout call, for an expired token or a 401) legitimately
// needs doRefresh() to persist so it can read the new token back out of
// localStorage; gating that path too would break #1393 PR 2 itself.
//
// Deliberately never reset back to false: there is no point at which we can
// know the abandoned request has truly given up (the browser's own network
// stack may hold it open far longer than our deadline), so once tripped it
// stays tripped for the rest of this page's lifetime -- which is fine,
// since a real browser navigation (to the IdP end_session URL, just below)
// tears down this module instance shortly after anyway.
let _logoutInProgress = false;

async function doFullLogout(): Promise<void> {
  dbgI(COMP, 'fullLogout: starting logout sequence');

  // Resolve the IdP end_session URL BEFORE any refresh/clearAuth touches
  // localStorage — a failed refreshSession() call also calls clearAuth(),
  // which removes oidc_issuer, and without it getSentinelLogoutUrl() falls
  // back to same-origin /oidc/end_session, which 404s and lets the IdP
  // session cookie survive (silent SSO re-grant on next /login).
  const logoutUrl = getSentinelLogoutUrl();
  const postLogoutRedirect = `${window.location.origin}/login`;
  const dest = `${logoutUrl}?post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirect)}`;

  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    // Bounded from the outside by SERVER_LOGOUT_DEADLINE_MS: attemptServerLogout
    // can refresh (unbounded fetch) and retry, and must never stall the
    // clearAuth()/navigate below past this overall deadline.
    const outcome = await withDeadline(attemptServerLogout(token), SERVER_LOGOUT_DEADLINE_MS);
    if (outcome === 'timeout') {
      // Giving up on attemptServerLogout — see _logoutInProgress above for
      // why this is where (and only where) that flag gets set.
      _logoutInProgress = true;
      dbgW(COMP, 'fullLogout: server-side logout attempt exceeded the deadline — proceeding anyway', {
        deadlineMs: SERVER_LOGOUT_DEADLINE_MS,
      });
    }
  } else {
    dbgW(COMP, 'fullLogout: no token in localStorage — skipping server-side revocation');
  }

  dbgI(COMP, 'fullLogout: redirecting to IdP end_session', { dest });
  clearAuth();
  window.location.href = dest;
}

/**
 * Full logout: revoke the session server-side, clear local state, then
 * redirect through Sentinel Auth /auth/logout to clear the HttpOnly session
 * cookie. Without the Sentinel logout the cookie persists and OIDC
 * auto-login immediately logs the user back in.
 *
 * The server-side revoke is AWAITED (with keepalive + a short per-request
 * timeout, an expired-token/401 refresh-and-retry, and an overall deadline
 * around the whole attempt) before we navigate away — a fire-and-forget
 * request here can be cancelled by the navigation before it ever leaves the
 * browser (#1393 defect 8). Whatever happens server-side, this always
 * clears local auth state and navigates: logout must never get stuck.
 *
 * Concurrent callers share one in-flight logout (see _fullLogoutPromise),
 * so calling this more than once before it resolves issues only one
 * POST /api/auth/logout.
 */
export async function fullLogout(): Promise<void> {
  if (_fullLogoutPromise) {
    dbgV(COMP, 'fullLogout: joining in-flight logout');
    return _fullLogoutPromise;
  }
  _fullLogoutPromise = doFullLogout().finally(() => {
    _fullLogoutPromise = null;
  });
  return _fullLogoutPromise;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

/** Decode a JWT payload without verifying the signature. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/** The tenant_slug claim carried by an access token.
 *
 * Read the TOKEN, never the locally-stored user object: after a context
 * switch those two can disagree, and the token is what the API actually
 * authorizes against. Returns null if the token is absent or unparseable.
 */
export function tenantSlugFromToken(token: string): string | null {
  const claims = decodeJwtPayload(token);
  const slug = claims?.tenant_slug;
  return typeof slug === 'string' ? slug : null;
}

/** Do two access tokens carry the SAME identity and context?
 *
 * Used by apiFetch (issue #1469) before replaying a request with a token that
 * replaced the one it was sent with: a replay is only safe when the new token
 * is a ROTATION of the same session, never a different user (shared station
 * tablets), tenant (Navbar tenant switch) or subtenant (SubtenantPicker).
 * Compares the user (`user_id`, the app's UUID, else `sub`), `tenant_slug`
 * and `subtenant_id` (absent on both = equal). FAIL-CLOSED: an undecodable
 * token, or one with no user or no tenant, is a mismatch.
 */
export function tokensShareIdentity(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  const ca = decodeJwtPayload(a);
  const cb = decodeJwtPayload(b);
  if (!ca || !cb || typeof ca !== 'object' || typeof cb !== 'object') return false;
  const who = (c: Record<string, unknown>): string | null => {
    const v = c.user_id ?? c.sub;
    return typeof v === 'string' && v !== '' ? v : typeof v === 'number' ? String(v) : null;
  };
  const ua = who(ca);
  const ub = who(cb);
  if (ua === null || ub === null || ua !== ub) return false;
  const ta = ca.tenant_slug;
  const tb = cb.tenant_slug;
  if (typeof ta !== 'string' || ta === '' || ta !== tb) return false;
  const sa = ca.subtenant_id ?? null;
  const sb = cb.subtenant_id ?? null;
  return sa === sb;
}

export function getAuth(): AuthState | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const userStr = localStorage.getItem(USER_KEY);
  dbgV(COMP, 'getAuth: localStorage read', {
    hasToken: !!token,
    tokenLength: token?.length ?? 0,
    hasUser: !!userStr,
  });
  if (!token || !userStr) {
    dbgV(COMP, 'getAuth: missing token or user — returning null');
    return null;
  }
  try {
    const user = JSON.parse(userStr);
    // Overlay JWT claims onto stored user so permission changes (role,
    // is_super_admin, capabilities) take effect on next token refresh
    // without requiring a full logout/login cycle.
    const jwt = decodeJwtPayload(token);
    if (jwt) {
      dbgV(COMP, 'getAuth: decoded JWT payload', {
        sub: jwt.sub,
        exp: jwt.exp,
        role: jwt.role,
        isSuperAdmin: jwt.is_super_admin,
        capCount: Array.isArray(jwt.capabilities) ? jwt.capabilities.length : 'n/a',
      });
      if (jwt.is_super_admin !== undefined) user.is_super_admin = !!jwt.is_super_admin;
      if (jwt.role !== undefined) user.role = jwt.role;
      if (jwt.capabilities !== undefined) user.capabilities = jwt.capabilities;
    } else {
      dbgW(COMP, 'getAuth: could not decode JWT payload — token may be malformed');
    }
    // Normalize user.id: OIDC-issued JWTs carry the user UUID under `sub` and
    // `user_id` but NOT `id`. Populate id from the first available source so
    // every consumer of auth.user.id gets a defined value without a full re-login.
    if (user.id == null) {
      const storedUser = user as { user_id?: string; sub?: string };
      const jwtClaims = jwt as { user_id?: string; sub?: string } | null;
      const derived =
        storedUser.user_id ??
        storedUser.sub ??
        jwtClaims?.user_id ??
        jwtClaims?.sub;
      if (derived != null) {
        user.id = String(derived);
        dbgV(COMP, 'getAuth: normalized user.id from claim', { id: user.id });
      } else {
        dbgW(COMP, 'getAuth: user.id absent and no sub/user_id claim found — id remains undefined');
      }
    }
    return { token, user };
  } catch (err) {
    dbgE(COMP, 'getAuth: failed to parse stored user JSON', { error: String(err) });
    return null;
  }
}

/** Decode the exp claim from a JWT without verifying the signature. */
function getTokenExp(token: string): number | null {
  const jwt = decodeJwtPayload(token);
  return jwt && typeof jwt.exp === 'number' ? jwt.exp : null;
}

/** Return true if the stored access token is present and not yet expired. */
export function isAuthenticated(): boolean {
  dbgV(COMP, 'isAuthenticated: called');
  const auth = getAuth();
  if (!auth) {
    dbgV(COMP, 'isAuthenticated: no auth state — returning false');
    return false;
  }
  const exp = getTokenExp(auth.token);
  const nowSec = Date.now() / 1000;
  if (exp !== null && nowSec >= exp) {
    dbgI(COMP, 'isAuthenticated: token EXPIRED', {
      exp,
      now: Math.round(nowSec),
      expiredAgoSec: Math.round(nowSec - exp),
    });
    return false;
  }
  dbgV(COMP, 'isAuthenticated: token valid', {
    exp,
    expiresInSec: exp !== null ? Math.round(exp - nowSec) : 'no-exp',
    email: auth.user.email,
  });
  return true;
}

/**
 * Return true if the stored token will expire within REFRESH_THRESHOLD_SEC.
 * Returns false if there is no token or it is already expired.
 */
export function tokenExpiresSoon(): boolean {
  const auth = getAuth();
  if (!auth) return false;
  const exp = getTokenExp(auth.token);
  if (exp === null) return false;
  const nowSec = Date.now() / 1000;
  const expiresSoon = exp > nowSec && exp - nowSec < REFRESH_THRESHOLD_SEC;
  if (expiresSoon) {
    dbgI(COMP, 'tokenExpiresSoon: token expiring soon — proactive refresh needed', {
      expiresInSec: Math.round(exp - nowSec),
      thresholdSec: REFRESH_THRESHOLD_SEC,
    });
  } else {
    dbgV(COMP, 'tokenExpiresSoon: token not expiring soon', {
      expiresInSec: exp > nowSec ? Math.round(exp - nowSec) : 'already-expired',
    });
  }
  return expiresSoon;
}

// Module-level singleton: all callers share one in-flight refresh so we never
// issue two POST /api/auth/refresh concurrently. Without this, the 401 retry
// path in client.ts races App.tsx proactive refresh and the WebSocket reconnect.
let _refreshPromise: Promise<RefreshOutcome> | null = null;

/**
 * doRefresh()'s three distinguishable outcomes:
 * - 'ok': the IdP accepted the refresh token; new tokens are saved.
 * - 'rejected': an AUTHORITATIVE answer that the refresh token ITSELF is no
 *   good — clearAuth() has already run, or (the logout race) already ran
 *   elsewhere. The ONLY status the backend uses for this is 401: the
 *   cxq-sentinel-client SDK's POST /refresh handler raises HTTPException(401)
 *   for both "Invalid or expired refresh token" (validate_refresh_token
 *   fails) and "Session refresh failed — please log in again" (the
 *   on_refresh hook fails) — see
 *   cxq-sentinel-auth/clients/python/src/cxq_sentinel_client/router.py:331-335
 *   and :344-352 (mounted at /api/auth by backend/app/main.py:692). A
 *   malformed 200 (no access_token) and the logout-race case are treated the
 *   same way, since both are also definitive "this attempt produced no usable
 *   session" answers, not ambiguous ones.
 * - 'transient': anything else that isn't 'ok' — a non-2xx status OTHER than
 *   401 (5xx, 429, a proxy's 502/504, etc.), or the request never getting an
 *   authoritative answer at all (a thrown exception). None of these say
 *   anything about whether the refresh token itself is good — a backend
 *   outage or an offline device must not wipe it.
 */
type RefreshOutcome = 'ok' | 'rejected' | 'transient';

async function doRefresh(): Promise<RefreshOutcome> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    dbgW(COMP, 'refreshSession: no refresh token in localStorage — cannot refresh');
    return 'rejected';
  }

  dbgI(COMP, 'refreshSession: attempting token refresh via POST /api/auth/refresh');
  const startMs = Date.now();

  // Deliberately NO AbortController/timeout here (see SERVER_LOGOUT_DEADLINE_MS's
  // comment above and _logoutInProgress's below): other callers of
  // refreshSession() -- most importantly client.ts's 401-retry path -- must
  // keep waiting on a slow-but-alive backend rather than have this shared
  // helper unilaterally decide the request is dead and abort it out from
  // under them. Callers that need a bound (attemptBootRefresh() below) wrap
  // this with withDeadline() instead, which only stops AWAITING — it never
  // cancels the underlying fetch, so a request that later succeeds still gets
  // its tokens saved normally.
  try {
    const resp = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const elapsed = Date.now() - startMs;
    dbgI(COMP, 'refreshSession: /api/auth/refresh responded', { status: resp.status, elapsedMs: elapsed });

    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      if (resp.status === 401) {
        dbgW(COMP, 'refreshSession: refresh REJECTED (401, invalid/expired refresh token) — clearing auth', { status: resp.status, body: body.slice(0, 200) });
        clearAuth();
        return 'rejected';
      }
      // Any other non-2xx (5xx, 429, a proxy error, ...) is the backend or
      // something in front of it having a bad moment, not an authoritative
      // statement that THIS refresh token is bad. Do not clear.
      dbgW(COMP, 'refreshSession: refresh failed with a non-authoritative status — NOT clearing auth', { status: resp.status, body: body.slice(0, 200) });
      return 'transient';
    }

    const data = await resp.json();
    if (!data.access_token) {
      dbgE(COMP, 'refreshSession: response OK but no access_token in body', { keys: Object.keys(data) });
      clearAuth();
      return 'rejected';
    }

    // A logout may have given up on waiting for this very call (or any
    // other in-flight refresh) while it was in flight -- see
    // _logoutInProgress's comment above fullLogout for why. Once that's
    // happened, local auth state has already been cleared and the page is
    // navigating away; persisting this (otherwise-valid) response would
    // silently resurrect the tokens fullLogout() just cleared. The network
    // call still succeeded, but nothing downstream can trust a token that
    // was never actually stored, so this reports failure like any other
    // refresh that didn't produce a usable session.
    if (_logoutInProgress) {
      dbgW(COMP, 'refreshSession: refresh succeeded but a logout already gave up on it — discarding, not storing');
      return 'rejected';
    }

    const auth = getAuth();
    if (auth) {
      dbgI(COMP, 'refreshSession: saving new access token', {
        hadRefresh: !!data.refresh_token,
        newTokenLength: data.access_token.length,
      });
      saveAuth(data.access_token, data.user ?? auth.user, data.refresh_token ?? refreshToken);
    } else {
      dbgW(COMP, 'refreshSession: getAuth() returned null after successful refresh — user was cleared mid-flight');
    }
    // Notify subscribers (WebSocket hook, etc.) that the token changed so
    // they can reconnect/rebind with the fresh credential.
    try {
      window.dispatchEvent(new Event('rk:token-changed'));
    } catch {
      // ignore — environments without window don't need the event
    }
    return 'ok';
  } catch (err) {
    // Deliberately does NOT clearAuth() here: a thrown exception (offline, DNS
    // failure, etc.) means we never got an authoritative answer about the
    // refresh token at all — it may well still be good. Discarding it here
    // would strand a volunteer who opened the app with no signal.
    dbgE(COMP, 'refreshSession: fetch threw an exception', { error: String(err) });
    return 'transient';
  }
}

/**
 * Attempt a token refresh using the stored refresh token.
 * Multiple concurrent callers share a single in-flight request — this is the
 * ONE place in the app that hits POST /api/auth/refresh. client.ts 401-retry,
 * App.tsx proactive refresh, and the WebSocket reconnect all call through here.
 *
 * On success, updates localStorage with the new access token and fires a
 * `rk:token-changed` window event. On failure, clears auth so the user is
 * redirected to login on next isAuthenticated() check.
 *
 * This is a thin boolean wrapper over refreshSessionDetailed() so existing
 * callers (which only ever cared about success/failure) are unaffected by the
 * 'rejected' vs. 'transient' distinction below — both are simply `false`
 * here, same as before this distinction existed.
 */
export async function refreshSession(): Promise<boolean> {
  return (await refreshSessionDetailed()) === 'ok';
}

/**
 * Same in-flight request as refreshSession() (shares the SAME `_refreshPromise`
 * singleton/dedupe — calling one while the other is in flight joins it, never
 * issues a second POST), but reports which kind of failure occurred instead of
 * collapsing both into `false`. attemptBootRefresh() below is the one caller
 * that needs to tell them apart: an authoritative rejection means the refresh
 * token is genuinely dead and should be discarded, but a transient failure
 * means we simply don't know yet and must NOT discard a possibly-still-good
 * token.
 */
export async function refreshSessionDetailed(): Promise<RefreshOutcome> {
  if (_refreshPromise) {
    dbgV(COMP, 'refreshSession: joining in-flight refresh');
    return _refreshPromise;
  }
  _refreshPromise = doRefresh().finally(() => {
    _refreshPromise = null;
  });
  return _refreshPromise;
}

/**
 * Bounds how long attemptBootRefresh() below will WAIT for a refresh before
 * giving up on this boot attempt (e.g. a captive portal or dead venue wifi
 * that never responds at all). This is a boot-path-only bound applied via
 * withDeadline() — it does NOT touch doRefresh()'s own fetch (see doRefresh's
 * comment: that request deliberately has no AbortController, because
 * client.ts's 401-retry path and others sharing the same _refreshPromise must
 * keep waiting on a slow-but-alive backend). Losing the race here just means
 * THIS caller stops waiting; the shared refresh keeps running in the
 * background, and if it later succeeds, doRefresh()'s normal success path
 * still saves the rotated tokens — so there's no risk of discarding a
 * refresh token the server already consumed and rotated.
 */
const BOOT_REFRESH_TIMEOUT_MS = 10_000;

/**
 * Boot-time / cold-load session check (issue #1508). The access token TTL is
 * 5 minutes, so on almost any reopen (app idle overnight, PWA backgrounded
 * and killed by the OS, etc.) isAuthenticated() is already false by the time
 * the page loads — even though a perfectly good 12-hour refresh token may
 * still be sitting in localStorage, unused. This tries the refresh token
 * BEFORE giving up on the session.
 *
 * - Access token still valid -> resolves true immediately, no network call.
 * - Access token expired/absent but a refresh token is stored -> attempts
 *   exactly one refresh via the shared refreshSessionDetailed() singleton
 *   above, so concurrent callers (two auth gates mounting in the same tick, a
 *   poll racing boot, etc.) join the SAME in-flight request rather than
 *   issuing a second POST /api/auth/refresh. Bounded to BOOT_REFRESH_TIMEOUT_MS
 *   via withDeadline() — see its comment for why that bound doesn't touch the
 *   underlying request. If any other caller (e.g. Login.tsx's own boot check)
 *   also calls this, it inherits the same bound, which is fine — nobody
 *   should be stuck on the boot splash/spinner forever.
 * - No refresh token at all -> resolves false immediately, no network call
 *   (today's behaviour, unchanged).
 *
 * Failure handling is NOT uniform, and that distinction matters:
 * - 'rejected' (an authoritative 401 — see RefreshOutcome's comment for the
 *   exact backend source — or a malformed 200) means the refresh token is
 *   genuinely dead. This clears any leftover tokens so a failed boot check
 *   lands on a clean, unambiguous unauthenticated state rather than one that
 *   gets silently retried against the network on every subsequent route
 *   mount.
 * - 'transient' (a 5xx/429/other non-401 status, a thrown network exception,
 *   OR this function's own BOOT_REFRESH_TIMEOUT_MS elapsing) means we never
 *   got an authoritative answer at all. This attempt still resolves false —
 *   the caller falls through to /login exactly as before — but storage is
 *   left untouched, so neither a mid-incident backend outage nor a volunteer
 *   opening the app with no signal loses an otherwise-good 12-hour refresh
 *   token; it's still there to try again on the next open (or, if the
 *   abandoned request itself later resolves, doRefresh() persists the
 *   rotated tokens then).
 */
export async function attemptBootRefresh(): Promise<boolean> {
  if (isAuthenticated()) return true;
  if (!getRefreshToken()) return false;
  const outcome = await withDeadline(refreshSessionDetailed(), BOOT_REFRESH_TIMEOUT_MS);
  if (outcome === 'timeout') {
    dbgW(COMP, 'attemptBootRefresh: timed out waiting for refresh — falling through to /login for this attempt; the refresh itself keeps running in the background', { timeoutMs: BOOT_REFRESH_TIMEOUT_MS });
    return false;
  }
  if (outcome === 'rejected') {
    clearAuth();
    return false;
  }
  return outcome === 'ok';
}

// ---------------------------------------------------------------------------
// PIN lock helpers
// ---------------------------------------------------------------------------

const PIN_HASH_KEY = 'responsekit_pin_hash';
const BIOMETRIC_CRED_KEY = 'responsekit_biometric_cred';

async function sha256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function hasPinSet(): boolean {
  return localStorage.getItem(PIN_HASH_KEY) !== null;
}

export async function setPin(pin: string): Promise<void> {
  const hash = await sha256(pin);
  localStorage.setItem(PIN_HASH_KEY, hash);
}

export async function verifyPin(pin: string): Promise<boolean> {
  const stored = localStorage.getItem(PIN_HASH_KEY);
  if (!stored) return false;
  const hash = await sha256(pin);
  return hash === stored;
}

export function clearPin(): void {
  localStorage.removeItem(PIN_HASH_KEY);
}

// ---------------------------------------------------------------------------
// Biometric (WebAuthn platform authenticator) helpers
// ---------------------------------------------------------------------------

export async function isBiometricAvailable(): Promise<boolean> {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export function hasBiometricRegistered(): boolean {
  return localStorage.getItem(BIOMETRIC_CRED_KEY) !== null;
}

export async function registerBiometric(): Promise<boolean> {
  try {
    const auth = getAuth();
    if (!auth) return false;

    const userId = new TextEncoder().encode(String(auth.user.id));
    const challenge = crypto.getRandomValues(new Uint8Array(32));

    const credential = await navigator.credentials.create({
      publicKey: {
        rp: { name: 'ResponseKit', id: window.location.hostname },
        user: {
          id: userId,
          name: auth.user.email,
          displayName: auth.user.name || auth.user.email,
        },
        challenge,
        pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'discouraged',
        },
        timeout: 60000,
      },
    }) as PublicKeyCredential | null;

    if (!credential) return false;

    const rawBytes = new Uint8Array(credential.rawId);
    let credId = '';
    for (let i = 0; i < rawBytes.length; i++) credId += String.fromCharCode(rawBytes[i]);
    credId = btoa(credId);
    localStorage.setItem(BIOMETRIC_CRED_KEY, credId);
    return true;
  } catch {
    return false;
  }
}

export async function verifyBiometric(): Promise<boolean> {
  try {
    const credIdB64 = localStorage.getItem(BIOMETRIC_CRED_KEY);
    if (!credIdB64) return false;

    const raw = atob(credIdB64);
    const credIdBytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) credIdBytes[i] = raw.charCodeAt(i);
    const challenge = crypto.getRandomValues(new Uint8Array(32));

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [{
          id: credIdBytes,
          type: 'public-key',
          transports: ['internal'],
        }],
        userVerification: 'required',
        timeout: 60000,
      },
    });

    return assertion !== null;
  } catch {
    return false;
  }
}

export function clearBiometric(): void {
  localStorage.removeItem(BIOMETRIC_CRED_KEY);
}

/** True while on the OIDC callback route, where the session is still being
 * established by AuthCallback. Token-touching side effects (proactive
 * refresh, profile fetch) must be suppressed here — they act on stale
 * leftover state and race the in-flight code exchange. */
export function isOnAuthCallback(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === '/auth/callback';
}

/**
 * Return true if the current user has the given capability.
 *
 * caps.includes('*') mirrors require_capability() in backend/app/auth.py:237
 * which checks "*" in caps or capability in caps.
 *
 * auth.user.is_super_admin does NOT mirror the backend (backend/app/auth.py:235-243
 * checks caps only, not is_super_admin). This clause is a graceful-degradation aid
 * for super-admins only: it fires when a stale stored-user object has is_super_admin
 * persisted but the capabilities array is undefined (e.g. old localStorage object from
 * before the capabilities JWT overlay). For all normal tokens where is_super_admin=true,
 * the caps array also contains "*" and the first clause fires instead.
 */
export function hasCapability(capability: string): boolean {
  const auth = getAuth();
  if (!auth) return false;
  const caps: string[] = auth.user.capabilities ?? [];
  return caps.includes('*') || caps.includes(capability) || !!auth.user.is_super_admin;
}
